import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.jsx';
import { localizeMessage } from '../utils/toast.js';

const RESEND_COOLDOWN_SECONDS = 60;

function VerifyEmail() {
  const { t } = useTranslation();
  const { verifyEmail, resendVerificationCode, redirectPath, clearRedirect } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (redirectPath) {
      clearRedirect();
      navigate(redirectPath, { replace: true });
    }
  }, [redirectPath, navigate, clearRedirect]);

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim() || !code.trim()) {
      setError(t('authPages.fillRequired'));
      return;
    }
    setLoading(true);
    try {
      const result = await verifyEmail(email.trim(), code.trim());
      if (!result.success) {
        setError(result.error || t('authPages.somethingWrong'));
      }
    } catch {
      setError(t('authPages.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError(t('authPages.enterEmail'));
      return;
    }
    setResending(true);
    try {
      const result = await resendVerificationCode(email.trim());
      if (result.success) {
        setInfo(t('authPages.codeResent'));
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError(result.error || t('authPages.somethingWrong'));
      }
    } catch {
      setError(t('authPages.somethingWrong'));
    } finally {
      setResending(false);
    }
  };

  return (
    <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f0', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 16, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <Link to="/registration" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#32614d', fontWeight: 600, fontSize: 13, textDecoration: 'none', marginBottom: 20 }}>
          <ArrowLeft size={15} /> {t('authPages.backToLogin')}
        </Link>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a3d2e', margin: '0 0 6px' }}>{t('authPages.confirmEmailTitle')}</h2>
        <p style={{ fontSize: 14, color: '#666', margin: '0 0 24px' }}>
          {t('authPages.confirmEmailSubtitle', { email: email || t('authPages.yourEmail') })}
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>{t('authPages.email')}</label>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <Mail size={16} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
              style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>{t('authPages.confirmationCode')}</label>
          <input
            type="text" inputMode="numeric" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000" required
            style={{
              width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #ddd',
              fontSize: 22, letterSpacing: 8, textAlign: 'center', fontWeight: 700,
              outline: 'none', boxSizing: 'border-box', marginBottom: 14,
            }}
          />

          {error && (
            <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 10, fontSize: 12, border: '1px solid #ffcdd2', marginBottom: 14 }}>
              {localizeMessage(error)}
            </div>
          )}
          {info && (
            <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 14px', borderRadius: 10, fontSize: 12, border: '1px solid #c8e6c9', marginBottom: 14 }}>
              {info}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: 13, borderRadius: 10, background: loading ? '#ccc' : 'linear-gradient(135deg,#32614d,#2a5240)', color: 'white', border: 'none', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? t('authPages.verifying') : t('authPages.confirmBtn')}
          </button>

          <button type="button" onClick={handleResend} disabled={resending || cooldown > 0}
            style={{ width: '100%', padding: 11, marginTop: 10, borderRadius: 10, background: 'none', border: 'none', color: (resending || cooldown > 0) ? '#aaa' : '#32614d', fontSize: 13, fontWeight: 600, cursor: (resending || cooldown > 0) ? 'not-allowed' : 'pointer' }}>
            {cooldown > 0
              ? t('authPages.resendCodeIn', { seconds: cooldown })
              : resending
                ? t('authPages.sending')
                : t('authPages.resendCode')}
          </button>
        </form>
      </div>
    </section>
  );
}

export default VerifyEmail;
